import { CoTrustedPartner } from 'Modules'
import {
  PtoV3CreateArgs,
  PtoV3PhaseArgs,
  PtoV3PoolDistributionArgs,
  PtoV3TimelineItem,
} from 'Modules'

import {
  PTO_V3_CONTRACT_ERROR_MESSAGES,
  PTO_V3_PHASE_ITEMS,
  ERC_PERCENT_DECIMALS,
  ERC_PERCENT_MAX_LENGTH,
} from 'constants/pto-v3'
import { customErrorContract } from 'hooks/contract/use-contract-viem'

/** struct IPTOOrchestrator.PTOParams, built from GET /admin/pto/:id/create-contract */
export const toCreatePtoParams = (args: PtoV3CreateArgs) => ({
  initialOwner: args.ownerAddress,
  salt: args.salt,
  name: args.contractName || args.ptoName,
  symbol: args.symbol,
  cap: BigInt(args.cap),
  identityActionKeys: args.identityActionKeys || [],
  identityClaimKeys: args.identityClaimKeys || [],
})

/**
 * struct IPTOOrchestrator.PoolDistributionParams, built from
 * GET /admin/pto/:id/create-pool-distribution. The BE already returns contract
 * units (pricePerPTO in USDC units, buyingFeeBP in basis points, whole PTOs).
 */
export const toPoolDistributionParams = (args: PtoV3PoolDistributionArgs) => ({
  ptoAddress: args.ptoAddress,
  initialOwner: args.initialOwner,
  salt: args.salt,
  pricePerPTO: BigInt(args.pricePerPTO),
  maxPTOAmount: BigInt(args.maxPTOAmount),
  fundingFeeBP: Number(args.buyingFeeBP || 0),
  claimTopic: BigInt(args.claimTopic),
  claimScheme: BigInt(args.claimScheme),
  claimIssuer: args.claimIssuer,
  claimSignature: args.claimSignature,
  claimData: args.claimData,
  claimUri: args.claimUri || '',
})

export const isSameAddress = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase()

/**
 * Timelines form rows -> PATCH /admin/pto/v3/:id/selling-details phases.
 * Only enabled phases are sent. phaseId is NOT sent: the BE numbers the purchase phases
 * itself (1, 2, ... per pool) on every save (PTO_DISTRIBUTION_UPGRADE_FE.md).
 */
export const toPhasesV3 = (phases?: PtoV3TimelineItem[]): PtoV3PhaseArgs[] =>
  (phases || [])
    .filter((phase) => phase.enabled)
    .map((phase) => {
      const maxPTOs = phase.isPurchasePhase ? Number(phase.maxPTOs || 0) : 0
      return {
        name: phase.name,
        startTime: phase.startTime || 0,
        endTime: phase.isEndTimeUnlimited ? 0 : phase.endTime || 0,
        isEndTimeUnlimited: !!phase.isEndTimeUnlimited,
        isPurchasePhase: !!phase.isPurchasePhase,
        isClaimPhase: !!phase.isClaimPhase,
        maxPTOs,
        maxPTOsWhitelisted: phase.name === 'whitelist' ? maxPTOs : 0,
        ptosReserved: Number(phase.ptosReserved || 0),
        tierLimits: [],
      }
    })

/**
 * Timelines form rows from the PHASE_ITEMS template, merged with the phases of
 * GET /admin/pto/v3/:id when the PTO already has selling details.
 * Without saved phases the template defaults apply (upcoming + fcfs on).
 */
export const initTimeLinesV3 = (phases?: PtoV3PhaseArgs[]): PtoV3TimelineItem[] => {
  const hasSaved = !!phases?.length
  return PTO_V3_PHASE_ITEMS.map((item) => {
    const phase = phases?.find((p) => p.name === item.name)
    return {
      phaseId: phase?.phaseId,
      name: item.name,
      required: item.required || false,
      // required phases (upcoming / fcfs) have no toggle, keep them on even if the BE omitted them
      enabled: !!phase || (hasSaved ? !!item.required : !!item.enabled),
      startTime: phase?.startTime || 0,
      endTime: phase?.endTime || 0,
      isEndTimeUnlimited: phase?.isEndTimeUnlimited ?? item.isEndTimeUnlimited,
      isPurchasePhase: phase?.isPurchasePhase ?? item.isPurchasePhase,
      isClaimPhase: phase?.isClaimPhase || false,
      maxPTOs: phase?.maxPTOs || 0,
      maxPTOsWhitelisted: phase?.maxPTOsWhitelisted || 0,
      ptosReserved: phase?.ptosReserved || 0,
    }
  })
}

export const INITIAL_CO_TRUSTED_PARTNER: CoTrustedPartner = {
  name: '',
  logo: '',
  website: '',
}

/**
 * Clean co-trusted partners - trim values and filter out empty items
 * @param partners - array of partners from form
 */
export const cleanCoTrustedPartners = (partners: any[]) => {
  if (!partners || partners.length === 0) return []

  return partners
    .map((item) => ({
      name: (item?.name || '').trim(),
      logo: (item?.logo || '').trim(),
      website: (item?.website || '').trim(),
    }))
    .filter((item) => item.name || item.logo || item.website)
}

/**
 * Normalize terms from API for form field (string).
 * Supports legacy plain string and current { content, flowType } shape.
 */
export const normalizeTermsContent = (terms: unknown): string => {
  if (terms == null) return ''
  if (typeof terms === 'string') return terms
  if (typeof terms === 'object' && terms !== null && 'content' in terms) {
    const content = (terms as { content?: unknown }).content
    return typeof content === 'string' ? content : ''
  }
  return ''
}

/**
 * Contract / wallet error -> message for the admin: a known revert name maps to its
 * friendly text, anything else falls back to the decoded viem message.
 */
export const describeV3ContractError = (error: unknown): string => {
  const raw = `${customErrorContract(error) || ''}`
  const key = Object.keys(PTO_V3_CONTRACT_ERROR_MESSAGES).find((name) => raw.includes(name))
  return key ? PTO_V3_CONTRACT_ERROR_MESSAGES[key] : raw
}

// form field name -> API field name (expectedReturnCalculator.*)
export const ERC_FIELDS = {
  ercSellingCost: 'estimatedSellingCost',
  ercConservativeRate: 'conservativeGrowthRate',
  ercBaseRate: 'baseGrowthRate',
  ercUpsideRate: 'upsideGrowthRate',
} as const

/** Input normalizer: keep digits and the first comma, cap decimals and length. */
export const normalizeErcPercentInput = (value?: string) => {
  if (!value) return value
  const cleaned = `${value}`.replace(/[^\d,]/g, '')
  const [intPart, ...rest] = cleaned.split(',')
  const result =
    rest.length > 0 ? `${intPart},${rest.join('').slice(0, ERC_PERCENT_DECIMALS)}` : intPart
  return result.slice(0, ERC_PERCENT_MAX_LENGTH)
}

const ERC_PERCENT_REGEX = new RegExp(`^\\d+(,\\d{1,${ERC_PERCENT_DECIMALS}})?$`)

export const isValidErcPercent = (value?: string) => ERC_PERCENT_REGEX.test(`${value || ''}`.trim())

// form field name of the Expected Return Calculator on/off switch (API: expectedReturnCalculator.enabled)
export const ERC_ENABLED_FIELD = 'ercEnabled'

/**
 * Form values -> API payload `expectedReturnCalculator`.
 * oneOf: `{ enabled: false }` or `{ enabled: true, ...all rate fields }`.
 */
export const buildExpectedReturnCalculator = (values: Record<string, any>) => {
  if (!values?.[ERC_ENABLED_FIELD]) return { enabled: false }
  return {
    enabled: true,
    ...Object.fromEntries(
      Object.entries(ERC_FIELDS).map(([formKey, apiKey]) => [
        apiKey,
        Number(`${values?.[formKey] ?? ''}`.trim().replace(',', '.')),
      ])
    ),
  }
}

/** API `expectedReturnCalculator` -> form values. */
export const ercToFormValues = (erc?: Record<string, any> | null) => ({
  [ERC_ENABLED_FIELD]: Boolean(erc?.enabled),
  ...Object.fromEntries(
    Object.entries(ERC_FIELDS).map(([formKey, apiKey]) => {
      const value = erc?.[apiKey]
      return [
        formKey,
        value === null || value === undefined || value === ''
          ? undefined
          : `${value}`.replace('.', ','),
      ]
    })
  ),
})
